import cx from 'classnames';
import React from 'react';
import Select, {components} from 'react-select';

import {LabelTheme, Label} from './Label';

interface IOption {
    value: number;
    label: string;
}

type OptionValue = null | string | number;

interface IBaseProps<T extends OptionValue> {
    label?: string;
    options: IOption[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    error?: string;
    hasError?: boolean;
    helpText?: string;
    isSkinsAndChampionsSelectBox?: boolean;
    isPackageSelectBox?: boolean;
    labelTheme?: LabelTheme;
}

interface ISimpleSelectProps<T extends OptionValue> extends IBaseProps<T> {
    value: null | T;
    onChange: (newValue: T) => void;
    multiple?: false;
}

interface IMultiSelectProps<T extends OptionValue> extends IBaseProps<T> {
    value: T[];
    onChange: (newValue: T[]) => void;
    multiple: true;
}

export function SelectInput<T extends OptionValue>(
    props: ISimpleSelectProps<T> | IMultiSelectProps<T>,
) {
    const {
        label,
        options,
        className,
        placeholder,
        disabled,
        multiple,
        helpText,
        error,
        hasError,
        isSkinsAndChampionsSelectBox,
        isPackageSelectBox,
        labelTheme,
    } = props;

    const hasErrorMessage = !!error;
    const inErrorState = hasErrorMessage || hasError;

    /**
     * Notify the parent of a value change. Handles both multiple/simple values
     */
    function handleChange(newValue: any, {action}: any) {
        switch (action) {
            case 'select-option':
                // If it's a multiple select, we have to return an updated array to the
                // parent (with/without the item that was clicked)
                if (props.multiple) {
                    props.onChange(newValue.map((v: any) => v.value));
                } else {
                    props.onChange(newValue.value);
                }
                break;
            case 'remove-value':
                if (props.multiple) {
                    props.onChange(newValue ? newValue.map((v: any) => v.value) : []);
                }
                break;
            case 'clear':
                if (props.multiple) {
                    props.onChange([]);
                }
                break;
            default:
                console.error(`Unhandled react-select action: ${action}`);
        }
    }

    const selectedOption = props.multiple
        ? props.value.map(v => options.find((option: any) => option.value === v))
        : options.find((option: any) => option.value === props.value);

    return (
        <div className={className}>
            <Label theme={labelTheme} error={inErrorState}>
                {label}
            </Label>

            <Select
                options={options}
                value={selectedOption}
                onChange={handleChange}
                className={cx('shadow-sm', isSkinsAndChampionsSelectBox || isPackageSelectBox ? 'text-xs' :  'text-md')}
                placeholder={placeholder}
                isDisabled={disabled}
                isMulti={multiple}
                components={{
                    DropdownIndicator: inErrorState ? ErrorIndicator : DropdownIndicator,
                    SingleValue,
                    Option,
                }}
                styles={{
                    dropdownIndicator: provided => ({
                        ...provided,
                        color: '#9fa6b2',
                        padding: '0px',
                    }),

                    indicatorSeparator: () => ({
                        background: 'none',
                    }),

                    indicatorsContainer: () => ({
                        padding: isSkinsAndChampionsSelectBox ? '2px' : '4px',
                    }),

                    valueContainer: () => ({
                        height: '30px',
                        marginLeft: '4px',
                    }),

                    menu: () => ({
                        background: '#262626',
                        color: 'grey',
                        position: 'absolute',
                        width: '100%',
                    }),

                    placeholder: ({color, ...provided}, state) => ({
                        ...provided,

                        ...(state.isDisabled
                            ? {
                                color: '#b2bec3',
                            }
                            : {color: 'grey'}),
                    }),

                    control: (provided, state) => {
                        let borderColor = isSkinsAndChampionsSelectBox ? '#262626' : isPackageSelectBox ? '#262626' : '#666';
                        if (state.isFocused) borderColor = '#a4cafe';
                        if (inErrorState) borderColor = '#ffccd4';
                        if (state.isDisabled) borderColor = '#f1f2f6';

                        return {
                            ...provided,

                            outline: 'none',
                            cursor: 'pointer',
                            minHeight: isPackageSelectBox ? '2.95rem' : '20px',
                            borderColor,
                            borderRadius: isPackageSelectBox ? '0.3rem' : '0.375rem',
                            background: isPackageSelectBox ? 'transparent' : 'rgba(25, 25, 28, 0.85)',
                            borderWidth: '2px',

                            '&:hover': {
                                borderColor,
                            },

                            ...(state.isFocused
                                ? {
                                    outline: 'none',
                                    borderColor,
                                    boxShadow: inErrorState
                                        ? '0 0 0 3px rgba(248, 180, 180, 0.45)'
                                        : '0 0 0 3px rgba(164, 202, 254, 0.45)',

                                    '&:hover': {
                                        borderColor,
                                    },
                                }
                                : {}),

                            ...(state.isDisabled
                                ? {
                                    backgroundColor: '#ffffff',
                                }
                                : {}),
                        };
                    },

                    option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isDisabled
                            ? undefined
                            : state.isSelected
                                ? '#f7b733'
                                : state.isFocused
                                    ? '#f7b733'
                                    : undefined,
                    }),

                    multiValue: styles => ({
                        ...styles,
                        borderRadius: 4,
                        background: 'linear-gradient(103deg, #E84393 0%, #FF4757 100%)',
                    }),

                    multiValueLabel: styles => ({
                        ...styles,
                        color: 'white',
                    }),

                    multiValueRemove: styles => ({
                        ...styles,
                        color: 'white',

                        ':hover': {
                            background: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                        },
                    }),
                }}
            />

            {helpText && <p className="mt-8 text-14 text-40">{helpText}</p>}
            {hasErrorMessage && <p className="mt-8 text-14 text-warning-red">{error}</p>}
        </div>
    );
}

SelectInput.displayName = 'SelectInput';

function DropdownIndicator(props: any) {
    const {
        isDisabled,
        selectProps: {menuIsOpen},
    } = props;
    return (
        <components.DropdownIndicator {...props}>
            <i
                className={cx(
                    menuIsOpen ? 'fas fa-caret-up' : 'fas fa-caret-down',
                    isDisabled ? 'text-40' : '#f7b733',
                    'text-18 text-xs mr-1',
                )}
            />
        </components.DropdownIndicator>
    );
}

function ErrorIndicator(props: any) {
    return (
        <components.DropdownIndicator {...props}>
            <svg
                className="h-20 w-20 text-warning-red"
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                />
            </svg>
        </components.DropdownIndicator>
    );
}

function SingleValue(props: any) {
    const {children, ...otherProps} = props;
    const {icon} = otherProps.data;

    return (
        <components.SingleValue {...otherProps}>
            {icon && <span className="mr-4">{icon}</span>}
            <span className="text-london-air-white">{children}</span>
        </components.SingleValue>
    );
}

function Option(props: any) {
    const {children, ...otherProps} = props;
    const {icon} = otherProps.data;

    return (
        <components.Option {...otherProps}>
            {icon && <span className="mr-4">{icon}</span>}
            {children}
        </components.Option>
    );
}
